import React from 'react';
import { Timer } from 'lucide-react';
import { Language } from '../../types';
import { themeMap } from '../../utils/calcTheme';
import { t } from '../../utils/i18n';
import StepperField from '../StepperField';

export interface QuickTime {
  id: string;
  labelNl: string;
  labelEn: string;
  h: number;
  m: number;
  s: number;
}

export const DEFAULT_TIME_PRESETS: QuickTime[] = [
  { id: '30m', labelNl: '30 min', labelEn: '30 min', h: 0, m: 30, s: 0 },
  { id: '45m', labelNl: '45 min', labelEn: '45 min', h: 0, m: 45, s: 0 },
  { id: '1h', labelNl: '1u', labelEn: '1h', h: 1, m: 0, s: 0 },
  { id: '1-5h', labelNl: '1,5u', labelEn: '1.5h', h: 1, m: 30, s: 0 },
  { id: '2h', labelNl: '2u', labelEn: '2h', h: 2, m: 0, s: 0 },
  { id: '2-5h', labelNl: '2,5u', labelEn: '2.5h', h: 2, m: 30, s: 0 },
  { id: '3h', labelNl: '3u', labelEn: '3h', h: 3, m: 0, s: 0 },
];

interface TimeInputCardProps {
  language: Language;
  hours: number;
  minutes: number;
  seconds: number;
  quickTimes?: QuickTime[];
  onTimeChange: (h: number, m: number, s: number) => void;
  triggerHaptic?: (duration?: number) => void;
}

/**
 * 2. Tijd-invoerkaart (Input)
 * - Oppervlakte: bg-slate-50 border border-slate-100 rounded-2xl
 * - Icoon-container: bg-orange-100 text-orange-600
 * - Actieve preset: bg-orange-500 text-white shadow-sm (oranje, gelijk aan tijd-knop en icoon)
 * - Inactieve presets: bg-slate-200 text-slate-600
 */
export default function TimeInputCard({
  language,
  hours,
  minutes,
  seconds,
  quickTimes = DEFAULT_TIME_PRESETS,
  onTimeChange,
  triggerHaptic = () => {},
}: TimeInputCardProps) {
  const styles = themeMap.time.inputCard;

  return (
    <div
      id="form-card-time"
      className={`p-5 sm:p-6 transition-all space-y-4 ${styles.container}`}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${styles.iconContainer}`}>
          <Timer className="w-4 h-4 stroke-[2.4]" />
        </div>
        <div>
          <span className="text-sm font-bold text-slate-900 dark:text-slate-100 block">
            {t(language, 'timeCardTitle')}
          </span>
        </div>
      </div>

      {/* Stepper Columns */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <StepperField
          idPrefix="time-h"
          unit={t(language, 'unitH')}
          value={hours}
          onIncrement={() => onTimeChange(hours + 1, minutes, seconds)}
          onDecrement={() => onTimeChange(Math.max(0, hours - 1), minutes, seconds)}
          onChange={(val) => onTimeChange(val, minutes, seconds)}
          min={0}
          max={99}
          colorScheme="neutral"
        />

        <StepperField
          idPrefix="time-m"
          unit={t(language, 'unitMin')}
          value={minutes}
          onIncrement={() => {
            if (minutes >= 59) {
              onTimeChange(hours + 1, 0, seconds);
            } else {
              onTimeChange(hours, minutes + 1, seconds);
            }
          }}
          onDecrement={() => {
            if (minutes <= 0 && hours > 0) {
              onTimeChange(hours - 1, 59, seconds);
            } else {
              onTimeChange(hours, Math.max(0, minutes - 1), seconds);
            }
          }}
          onChange={(val) => onTimeChange(hours, val, seconds)}
          min={0}
          max={59}
          colorScheme="neutral"
        />

        <StepperField
          idPrefix="time-s"
          unit={t(language, 'unitSec')}
          value={seconds}
          onIncrement={() => {
            if (seconds >= 59) {
              onTimeChange(hours, minutes + 1, 0);
            } else {
              onTimeChange(hours, minutes, seconds + 1);
            }
          }}
          onDecrement={() => {
            if (seconds <= 0 && minutes > 0) {
              onTimeChange(hours, minutes - 1, 59);
            } else {
              onTimeChange(hours, minutes, Math.max(0, seconds - 1));
            }
          }}
          onChange={(val) => onTimeChange(hours, minutes, val)}
          min={0}
          max={59}
          colorScheme="neutral"
        />
      </div>

      {/* Presets directly under the time inputs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 pt-0.5 scrollbar-none">
        {quickTimes.map((qt) => {
          const isMatch = hours === qt.h && minutes === qt.m && seconds === qt.s;
          const displayLabel = language === 'nl' ? qt.labelNl : qt.labelEn;
          return (
            <button
              key={qt.id}
              id={`btn-quick-time-${qt.id}`}
              type="button"
              onClick={() => {
                triggerHaptic(10);
                onTimeChange(qt.h, qt.m, qt.s);
              }}
              className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-bold transition-all select-none cursor-pointer ${
                isMatch ? styles.activePreset : styles.inactivePreset
              }`}
            >
              {displayLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}
